import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

type SharedSelectedItemsContext = {
  selectedFiles: string[];
  selectedDirectories: string[];
  toggleSingleRow: () => void;
  toggleAllCheckboxes: (checked: boolean) => void;
  hasSelectedItem: () => boolean;
  globalCheckboxRef: React.RefObject<HTMLInputElement | null> | null;
  removeDeletedItemFromSelected: (id: string) => void;
};

const SharedSelectedItemsContext = React.createContext<SharedSelectedItemsContext>({
  selectedFiles: [],
  selectedDirectories: [],
  toggleSingleRow: () => {},
  toggleAllCheckboxes: () => {},
  hasSelectedItem: () => false,
  globalCheckboxRef: null,
  removeDeletedItemFromSelected: () => {},
});

export const useSharedFilesSelectedItemsContext = () => React.useContext(SharedSelectedItemsContext);

const DATA_ROW_ENTITY_ID = 'data-entity-id';
const DATA_ROW_ENTITY_TYPE = 'data-entity-type';

/**
 * Context Provider around Shared Tables to keep track of selected files and directories.
 * Checkboxes inside rows should contain the data-entity attributes.
 * Checked html state of checkboxes determines selection.
 */
export function SharedFilesSelectedItemsProvider({ children }: PropsWithChildren) {
  const [selectedFiles, setSelectedFiles] = useState<SharedSelectedItemsContext['selectedFiles']>([]);
  const [selectedDirs, setSelectedDirs] = useState<SharedSelectedItemsContext['selectedDirectories']>([]);

  const globalCheckboxRef = useRef<HTMLInputElement>(null);

  /**
   * Get all row checkboxes inside the tbody element.
   */
  const getAllRowCheckboxes = () => {
    // should we use a ref for better querying the row checkboxes ?
    const rowCheckboxes = document.querySelectorAll(`tbody [${DATA_ROW_ENTITY_ID}]`);
    return rowCheckboxes as NodeListOf<HTMLInputElement>;
  };

  /**
   * Update state with selected files and directories.
   */
  const updateSelectedItems = React.useCallback(() => {
    const files = [] as string[];
    const dirs = [] as string[];

    getAllRowCheckboxes().forEach((row) => {
      if (!row.checked) {
        return;
      }

      const sharedEntityType = row.getAttribute(DATA_ROW_ENTITY_TYPE);
      const sharedEntityId = row.getAttribute(DATA_ROW_ENTITY_ID);

      if (sharedEntityId === null) {
        return;
      }

      if (sharedEntityType === 'directory') {
        dirs.push(sharedEntityId);
      } else {
        files.push(sharedEntityId);
      }
    });

    setSelectedFiles(Array.from(files));
    setSelectedDirs(Array.from(dirs));
  }, []);

  const removeDeletedItemFromSelected = (id: string) => {
    if (selectedFiles?.length > 0 && selectedFiles.includes(id)) {
      setSelectedFiles(selectedFiles.filter(e => e !== id));
    }

    if (selectedDirs?.length > 0 && selectedDirs.includes(id)) {
      setSelectedDirs(selectedDirs.filter(e => e !== id));
    }
  };

  /**
   * Toggles a row as selected and sets global checkbox to false
   */
  const toggleSingleRow = () => {
    if (globalCheckboxRef.current) {
      globalCheckboxRef.current.checked = false;
    }
    updateSelectedItems();
  };

  /**
   * Toggles all checkboxes and corresponding rows.
   */
  const toggleAllCheckboxes = (checked: boolean): void => {
    getAllRowCheckboxes().forEach((el) => {
      el.checked = checked;
    });
    updateSelectedItems();
  };

  const hasSelectedItem = (): boolean => selectedFiles.length > 0 || selectedDirs.length > 0;

  /**
   * Updates selected items on Page Change.
   * Firefox / chrome handle checkbox states differently,
   * so we need to update the state according to checked items on refresh.
   */
  useEffect(() => {
    updateSelectedItems();
  }, [updateSelectedItems]);

  return (
    <SharedSelectedItemsContext.Provider
      value={{
        selectedFiles: selectedFiles,
        selectedDirectories: selectedDirs,
        toggleSingleRow: toggleSingleRow,
        toggleAllCheckboxes: toggleAllCheckboxes,
        hasSelectedItem: hasSelectedItem,
        globalCheckboxRef: globalCheckboxRef,
        removeDeletedItemFromSelected: removeDeletedItemFromSelected,
      }}
    >
      {children}
    </SharedSelectedItemsContext.Provider>
  );
}
